import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Camera } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { pickAndUpload } from "@/lib/upload";
import { colors, groupThemes, initials, gradientCss } from "@/lib/theme";
import { Field, Input, PrimaryButton } from "@/components/peeps/ui";
import { HomeButton } from "@/components/peeps/HomeButton";

export default function CreateGroup() {
  const [name, setName] = useState("");
  const [theme, setTheme] = useState("violet");
  const [cover, setCover] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.is_app_admin;

  const choosePhoto = async () => {
    setUploading(true);
    try {
      const res = await pickAndUpload();
      if (res && res.error) toast(res.error);
      else if (res && res[0]) setCover(res[0]);
    } catch {
      toast("Couldn't upload photo");
    } finally {
      setUploading(false);
    }
  };

  const create = async () => {
    setSaving(true);
    try {
      const res = await api.createGroup({ name: name.trim(), theme, cover_image_url: cover });
      if (res && res.pending) {
        toast("Request sent — the admin will review your group shortly.");
        navigate("/groups", { replace: true });
      } else {
        navigate(`/group/${res.id}`, { replace: true });
      }
    } catch (e) {
      toast(e.message || "Couldn't create group");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col max-w-2xl mx-auto" style={{ background: colors.surface }}>
      <div className="flex items-center justify-between px-3 py-3">
        <div className="flex items-center gap-1">
          <button data-testid="create-group-back" onClick={() => navigate(-1)} className="p-2"><ChevronLeft size={20} color={colors.onSurface} /></button>
          <HomeButton size={30} bg={colors.surfaceSecondary} />
        </div>
        <h1 className="font-display font-bold text-[17px]" style={{ color: colors.onSurface }}>New group</h1>
        <div className="w-9" />
      </div>

      <div className="flex-1 p-4 overflow-y-auto">
        {!isAdmin && (
          <div data-testid="approval-notice" className="mb-4 rounded-xl p-3.5" style={{ background: colors.brandTertiary, border: `1px solid ${colors.brandSecondary}44` }}>
            <p className="font-display font-bold text-[13.5px]" style={{ color: colors.brandSecondary }}>New groups need admin approval</p>
            <p className="font-display text-[12.5px] mt-1" style={{ color: colors.onSurfaceTertiary }}>
              Set up your group below and submit it — the peeps admin will review your request, and you'll be notified once it's approved.
            </p>
          </div>
        )}
        <Field label="Cover photo">
          <button data-testid="cover-picker" onClick={choosePhoto} disabled={uploading} className="relative w-full h-32 rounded-xl overflow-hidden flex items-center justify-center" style={cover ? {} : { background: gradientCss(groupThemes[theme].grad) }}>
            {cover ? <img src={mediaUrl(cover)} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <span className="font-display font-bold text-3xl" style={{ color: "rgba(255,255,255,0.9)" }}>{initials(name || "New")}</span>}
            <span className="absolute flex items-center justify-center rounded-full" style={{ width: 30, height: 30, bottom: 10, right: 10, background: colors.brand }}>
              <Camera size={14} color={colors.onBrand} />
            </span>
          </button>
        </Field>

        <Field label="Group name">
          <Input testID="group-name-input" placeholder="Beach House Crew" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>

        <Field label="Color">
          <div className="flex gap-3 flex-wrap">
            {Object.keys(groupThemes).map((key) => (
              <button key={key} data-testid={`theme-${key}`} onClick={() => setTheme(key)} className="p-[3px] rounded-full" style={{ border: `2px solid ${theme === key ? colors.onSurface : "transparent"}` }}>
                <span className="block rounded-full" style={{ width: 38, height: 38, background: gradientCss(groupThemes[key].grad) }} />
              </button>
            ))}
          </div>
        </Field>
      </div>

      <div className="p-4" style={{ borderTop: `1px solid ${colors.border}` }}>
        <PrimaryButton testID="create-group-submit" label={uploading ? "Uploading photo…" : (isAdmin ? "Create group" : "Request group")} onClick={create} disabled={!name.trim() || uploading} loading={saving} />
      </div>
    </div>
  );
}
